import { useCallback, useEffect, useRef, useState } from 'react';
import { AgeVerificationService } from '@/services/auth/AgeVerificationService';
import type { AgeVerificationStatus } from '@/services/auth/AgeVerificationService';
import { FEATURE_FLAGS } from '@/utils/featureFlags';
import { supabase } from '@/lib/supabaseGuard';
import type { AuthResolution } from '@/utils/authSessionGuard';
import {
  acceptAgeVerificationResult,
  planAgeVerificationFetch,
  type AgeSessionStatus,
} from '@/utils/ageVerificationSession';

export function useAgeVerificationStatus(
  userId: string | null | undefined,
  authResolution: AuthResolution
) {
  const [status, setStatus] = useState<AgeSessionStatus>('checking');
  const statusRef = useRef<AgeSessionStatus>(status);
  const userRef = useRef<string | null>(null);
  const generationRef = useRef(0);

  const setStatusAtomic = useCallback((next: AgeSessionStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const nextUserId = userId ?? null;
    const plan = planAgeVerificationFetch({
      previousUserId: userRef.current,
      nextUserId,
      currentStatus: statusRef.current,
      authResolution,
    });

    if (plan.userId !== undefined) {
      userRef.current = plan.userId;
    }
    if (plan.bumpGeneration) {
      generationRef.current += 1;
    }
    if (plan.status !== undefined) {
      setStatusAtomic(plan.status);
    }
    if (!plan.shouldFetch || !nextUserId) {
      return;
    }

    const requestUserId = nextUserId;
    const requestGeneration = ++generationRef.current;
    let cancelled = false;

    const applyIncoming = (incoming: AgeVerificationStatus) => {
      if (cancelled) return;
      const accepted = acceptAgeVerificationResult({
        requestUserId,
        activeUserId: userRef.current,
        requestGeneration,
        activeGeneration: generationRef.current,
        currentStatus: statusRef.current,
        incoming,
      });
      if (!accepted) return;
      setStatusAtomic(accepted);
    };

    void (async () => {
      if (FEATURE_FLAGS.AGE_GATE_SESSION_BYPASS) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled || requestGeneration !== generationRef.current) return;
        if (session) {
          console.log('⚠️ AGE_GATE_SESSION_BYPASS: session present, skipping DB age check');
          if (statusRef.current !== 'verified') {
            setStatusAtomic('verified');
          }
          return;
        }
      }

      const incoming = await AgeVerificationService.getVerificationStatus(requestUserId);
      applyIncoming(incoming);
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, authResolution, setStatusAtomic]);

  return {
    status,
    setStatusAtomic,
    statusRef,
    userRef,
  };
}
